import { flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { type Editor } from "@tiptap/core";
import { Divider, Space } from "antd";
import { useCallback, useLayoutEffect, useMemo } from "react";

import { routes } from "utilities/routes";

import { BTExternalLink } from "commonComponents/btWrappers/BTExternalLink/BTExternalLink";
import {
    BTIconExternalLink,
    BTIconLinkBreak,
    BTIconTrash,
} from "commonComponents/btWrappers/BTIcon";
import { getDOMPositionOfSelection } from "commonComponents/btWrappers/editor/core/editor.utils";
import { EditorControlButton } from "commonComponents/btWrappers/editor/core/EditorControlBar/common/EditorControlButton/EditorControlButton";
import { type IEntityMetadata } from "commonComponents/btWrappers/editor/core/extensions/custom/entityLink/EntityLinkNodeView";

import "./EntityLinkOptionsOverlay.less";

interface IEntityLinkOptionsOverlayProps {
    editor: Editor;
    node: Editor["state"]["doc"];
    metadata: IEntityMetadata;
}

export function EntityLinkOptionsOverlay({
    editor,
    node,
    metadata,
}: IEntityLinkOptionsOverlayProps) {
    const { x, y, strategy, refs } = useFloating({
        middleware: [flip(), shift({ padding: 5 }), offset(5)],
    });

    const handleCalculatePosition = useCallback(() => {
        return getDOMPositionOfSelection(editor);
    }, [editor]);
    useLayoutEffect(() => {
        refs.setReference({ getBoundingClientRect: handleCalculatePosition });
    }, [handleCalculatePosition, refs]);

    const href = useMemo(() => {
        if (!node.attrs.externalId) {
            throw new Error("externalId is not defined");
        }
        return routes.entityLink.getFullLink(node.attrs.externalId);
    }, [node.attrs.externalId]);

    return (
        <div
            ref={(ref) => refs.setFloating(ref)}
            className="EntityLinkOptionsOverlay"
            style={{
                position: strategy,
                top: y ?? undefined,
                left: x ?? undefined,
            }}
            tabIndex={0}
        >
            <Space size={2} split={<Divider type="vertical" className="OptionDivider" />}>
                {!metadata.entityRecord.isDeleted && (
                    <BTExternalLink href={href}>
                        <EditorControlButton
                            data-testid="openEntityLink"
                            friendlyName="Open in new tab"
                            icon={<BTIconExternalLink />}
                            isToggled={false}
                        />
                    </BTExternalLink>
                )}
                <EditorControlButton
                    data-testid="breakEntityLink"
                    friendlyName="Convert to URL"
                    icon={<BTIconLinkBreak />}
                    isToggled={false}
                    onClick={() => {
                        editor.chain().convertEntityLinkToLink(href, href).run();
                    }}
                />
                <EditorControlButton
                    data-testid="deleteEntityLink"
                    friendlyName="Delete"
                    icon={<BTIconTrash />}
                    disabled={!editor.can().chain().focus().deleteSelection().run()}
                    isToggled={false}
                    onClick={() => editor.chain().focus().deleteSelection().run()}
                />
            </Space>
        </div>
    );
}
